import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import { apiFetch } from '../../../utils/api';
import { formatCurrency } from '../../../utils/currency';

const blankProforma = {
  customer_id: '',
  site_id: '',
  valid_until: '',
  due_date: '',
  status: 'draft',
  customer_po_number: '',
  notes: '',
  terms: 'Payment due before invoice issue.\nAll prices exclude VAT unless stated.\nProforma valid for 30 days from date of issue.',
  remittance_email: '',
  items: []
};

function ProformaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [proforma, setProforma] = useState(blankProforma);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState('');

  useEffect(() => {
    fetchCustomers();
    fetchCompanySettings();
    if (isNew) {
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + 30);
      setProforma({
        ...blankProforma,
        valid_until: validUntil.toISOString().split('T')[0]
      });
      setLoading(false);
    } else {
      fetchProforma();
    }
  }, [id]);

  useEffect(() => {
    if (proforma.customer_id) {
      fetchSites(proforma.customer_id);
    } else {
      setSites([]);
    }
  }, [proforma.customer_id]);

  useEffect(() => {
    if (!proforma.remittance_email && companySettings.bank_remittance_email) {
      setProforma((prev) => ({ ...prev, remittance_email: companySettings.bank_remittance_email }));
    }
  }, [companySettings.bank_remittance_email]);

  const totals = useMemo(() => {
    const subtotal = (proforma.items || []).reduce((sum, item) => {
      return sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0));
    }, 0);
    const vatAmount = subtotal * 0.2;
    return { subtotal, vatAmount, total: subtotal + vatAmount };
  }, [proforma.items]);

  const bankDetails = useMemo(() => {
    const parts = [];
    if (companySettings.bank_name) parts.push(`Bank: ${companySettings.bank_name}`);
    if (companySettings.bank_account_name) parts.push(`Account Name: ${companySettings.bank_account_name}`);
    if (companySettings.bank_sort_code) parts.push(`Sort Code: ${companySettings.bank_sort_code}`);
    if (companySettings.bank_account_number) parts.push(`Account Number: ${companySettings.bank_account_number}`);
    if (companySettings.bank_iban) parts.push(`IBAN: ${companySettings.bank_iban}`);
    if (companySettings.bank_swift) parts.push(`SWIFT: ${companySettings.bank_swift}`);
    return parts;
  }, [companySettings]);

  const fetchCompanySettings = async () => {
    try {
      const response = await apiFetch('/api/company-settings');
      const data = await response.json();
      setCompanySettings(data || {});
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSites = async (customerId) => {
    try {
      const response = await apiFetch(`/api/sites?customer_id=${customerId}`);
      const data = await response.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const normaliseDates = (data) => {
    ['proforma_date', 'valid_until', 'due_date'].forEach((key) => {
      if (data[key]) data[key] = data[key].split('T')[0];
    });
    return data;
  };

  const fetchProforma = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await apiFetch(`/api/proformas/${id}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Proforma load failed with status ${response.status}`);
      }
      const data = await response.json();
      setProforma({ ...blankProforma, ...normaliseDates(data), items: data.items || [] });
    } catch (error) {
      console.error('Error fetching proforma:', error);
      setLoadError(error.response?.data?.error || error.message || 'Could not load proforma');
    } finally {
      setLoading(false);
    }
  };

  const setField = (field, value) => {
    setProforma((prev) => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    setProforma((prev) => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { description: '', category: 'parts', quantity: 1, unit: 'each', unit_price: 0 }
      ]
    }));
  };

  const updateItem = (index, field, value) => {
    setProforma((prev) => {
      const items = [...(prev.items || [])];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setProforma((prev) => ({
      ...prev,
      items: (prev.items || []).filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const saveProforma = async () => {
    if (!proforma.customer_id) {
      alert('Please select a customer');
      return;
    }

    setSaving(true);
    try {
      const response = await apiFetch(isNew ? '/api/proformas' : `/api/proformas/${id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(proforma)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save proforma');
      }
      const data = await response.json();
      if (isNew && data.id) {
        navigate(`/finance/proformas/${data.id}`);
      } else {
        fetchProforma();
      }
    } catch (error) {
      console.error('Error saving proforma:', error);
      alert(error.message || 'Failed to save proforma');
    } finally {
      setSaving(false);
    }
  };

  const convertProforma = async (target) => {
    setConverting(target);
    try {
      const response = await apiFetch(`/api/proformas/${id}/convert-to-${target}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Failed to convert to ${target}`);
      if (target === 'invoice') {
        navigate(`/finance/invoices/${data.invoice_id}`);
      } else {
        navigate(`/jobs/${data.job_id}`);
      }
    } catch (error) {
      console.error(`Error converting proforma to ${target}:`, error);
      alert(error.message || `Failed to convert to ${target}`);
    } finally {
      setConverting('');
    }
  };

  const deleteProforma = async () => {
    if (!window.confirm('Delete this proforma?')) return;
    try {
      const response = await apiFetch(`/api/proformas/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete proforma');
      }
      navigate('/finance/proformas');
    } catch (error) {
      console.error('Error deleting proforma:', error);
      alert(error.message || 'Failed to delete proforma');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Loading Proforma" backButton onBack={() => navigate('/finance/proformas')} sticky />
        <main className="max-w-4xl mx-auto px-6 py-8 text-gray-600">Loading...</main>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Proforma Could Not Load" backButton onBack={() => navigate('/finance/proformas')} sticky />
        <main className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="font-semibold text-red-900 mb-2">There was a problem loading this proforma.</div>
            <div className="text-red-800 mb-4">{loadError}</div>
            <button onClick={fetchProforma} className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium">
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={isNew ? 'New Proforma' : `Proforma ${proforma.proforma_ref || ''}`}
        subtitle={proforma.status || 'draft'}
        backButton
        onBack={() => navigate('/finance/proformas')}
        backTitle="Back to Proformas"
        sticky
        actionButton={
          <div className="flex gap-2">
            {!isNew && !proforma.job_id && (
              <button
                onClick={() => convertProforma('job')}
                disabled={!!converting}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {converting === 'job' ? 'Converting...' : 'Convert to Job'}
              </button>
            )}
            {!isNew && !proforma.invoice_id && (
              <button
                onClick={() => convertProforma('invoice')}
                disabled={!!converting}
                className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {converting === 'invoice' ? 'Converting...' : 'Convert to Invoice'}
              </button>
            )}
            {!isNew && (
              <button onClick={deleteProforma} className="px-3 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700">
                Delete
              </button>
            )}
          </div>
        }
      />

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        <section className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Customer</span>
              <select
                value={proforma.customer_id || ''}
                onChange={(event) => setField('customer_id', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">Select customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.company_name}</option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Site</span>
              <select
                value={proforma.site_id || ''}
                onChange={(event) => setField('site_id', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="">Head Office</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.site_name}</option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Valid Until</span>
              <input
                type="date"
                value={proforma.valid_until || ''}
                onChange={(event) => setField('valid_until', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>

            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Due Date</span>
              <input
                type="date"
                value={proforma.due_date || ''}
                onChange={(event) => setField('due_date', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>

            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Status</span>
              <select
                value={proforma.status || 'draft'}
                onChange={(event) => setField('status', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="converted">Converted</option>
                <option value="invoiced">Invoiced</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>

            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Customer PO</span>
              <input
                value={proforma.customer_po_number || ''}
                onChange={(event) => setField('customer_po_number', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>

            <label className="text-sm md:col-span-2">
              <span className="block font-medium text-gray-700 mb-1">Remittance Email</span>
              <input
                type="email"
                value={proforma.remittance_email || ''}
                onChange={(event) => setField('remittance_email', event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">Line Items</h2>
            <button onClick={addItem} className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium">
              Add Line
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2 w-28">Category</th>
                  <th className="text-right px-3 py-2 w-24">Qty</th>
                  <th className="text-left px-3 py-2 w-24">Unit</th>
                  <th className="text-right px-3 py-2 w-32">Unit Price</th>
                  <th className="text-right px-3 py-2 w-32">Line Total</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(proforma.items || []).length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-3 py-6 text-center text-gray-500">No line items yet.</td>
                  </tr>
                ) : (
                  proforma.items.map((item, index) => {
                    const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                    return (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <input
                            value={item.description || ''}
                            onChange={(event) => updateItem(index, 'description', event.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={item.category || ''}
                            onChange={(event) => updateItem(index, 'category', event.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity || ''}
                            onChange={(event) => updateItem(index, 'quantity', event.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            value={item.unit || ''}
                            onChange={(event) => updateItem(index, 'unit', event.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price || ''}
                            onChange={(event) => updateItem(index, 'unit_price', event.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(lineTotal)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => removeItem(index)} className="text-red-600 hover:text-red-800 text-xs font-medium">
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <label className="block text-sm">
              <span className="block font-medium text-gray-700 mb-1">Notes</span>
              <textarea
                value={proforma.notes || ''}
                onChange={(event) => setField('notes', event.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-gray-700 mb-1">Terms</span>
              <textarea
                value={proforma.terms || ''}
                onChange={(event) => setField('terms', event.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
            </label>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div>
              <h2 className="font-semibold text-gray-900 mb-2">Totals</h2>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(totals.subtotal)}</span></div>
                <div className="flex justify-between"><span>VAT</span><span>{formatCurrency(totals.vatAmount)}</span></div>
                <div className="flex justify-between text-lg font-semibold border-t pt-2"><span>Total</span><span>{formatCurrency(totals.total)}</span></div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="font-semibold text-gray-900 mb-2">Bank Details</h2>
              {bankDetails.length === 0 ? (
                <p className="text-sm text-gray-500">No company bank details configured.</p>
              ) : (
                <div className="text-sm text-gray-700 space-y-1">
                  {bankDetails.map((line) => <div key={line}>{line}</div>)}
                </div>
              )}
            </div>

            <button
              onClick={saveProforma}
              disabled={saving}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Proforma'}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ProformaDetail;
